/**
 * I18-T.1: Lightweight i18n provider with lazy-loaded locale files.
 *
 * - Reads locale from localStorage (`ease_locale`) with navigator.language fallback
 * - Lazy-loads locale JSON via dynamic import (not bundled in main chunk)
 * - Fallback chain: selected locale -> English -> raw key
 * - No external libraries
 */
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/** Matches the runtime locale bundles shipped from ./locales/*.json */
export type SupportedLanguage = "en" | "de" | "fr" | "ro" | "es" | "nl";

const STORAGE_KEY = "ease_locale";
const DEFAULT_LOCALE: SupportedLanguage = "en";
const SUPPORTED: ReadonlySet<string> = new Set<SupportedLanguage>(["en", "de", "fr", "ro", "es", "nl"]);

type LocaleMessages = Record<string, unknown>;
type TranslationParams = Record<string, string | number | boolean | null | undefined>;

interface I18nContextValue {
  locale: SupportedLanguage;
  setLocale: (l: SupportedLanguage) => void;
  t: (key: string, params?: TranslationParams) => string;
  isReady: boolean;
}

export const I18nContext = createContext<I18nContextValue>({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key: string) => key,
  isReady: false,
});

function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template;

  return template.replace(/\{\{(\w+)\}\}/g, (match, key: string) => {
    const value = params[key];
    return value === null || value === undefined ? match : String(value);
  });
}

/**
 * Resolve a dot-separated key against a nested object.
 * "pricing.free.name" -> messages.pricing.free.name
 */
function resolve(messages: LocaleMessages, key: string): string | undefined {
  const parts = key.split(".");
  let current: unknown = messages;
  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== "object") {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === "string" ? current : undefined;
}

/**
 * Detect initial locale from localStorage or navigator.language.
 * Returns a supported language code or the default.
 */
function detectLocale(): SupportedLanguage {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.has(stored)) {
      return stored as SupportedLanguage;
    }
  } catch {
    // localStorage unavailable (SSR, privacy mode)
  }

  const browserLang = navigator.language.split("-")[0] ?? "en";
  if (SUPPORTED.has(browserLang)) {
    return browserLang as SupportedLanguage;
  }

  return DEFAULT_LOCALE;
}

/**
 * Dynamic import for locale JSON files.
 * Vite splits each locale into its own chunk.
 */
async function loadMessages(locale: SupportedLanguage): Promise<LocaleMessages> {
  const modules: Record<string, () => Promise<{ default: LocaleMessages }>> = {
    en: () => import("./locales/en.json"),
    nl: () => import("./locales/nl.json"),
    de: () => import("./locales/de.json"),
    fr: () => import("./locales/fr.json"),
    es: () => import("./locales/es.json"),
    ro: () => import("./locales/ro.json"),
  };

  const loader = modules[locale];
  if (!loader) {
    // Unsupported locale with no file yet — fall back to English
    const enLoader = modules["en"];
    if (!enLoader) return {};
    const fallback = await enLoader();
    return fallback.default;
  }

  const mod = await loader();
  return mod.default;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<SupportedLanguage>(detectLocale);
  const [messages, setMessages] = useState<LocaleMessages>({});
  const [fallbackMessages, setFallbackMessages] = useState<LocaleMessages>({});
  const [isReady, setIsReady] = useState(false);

  // Load English as fallback on mount
  useEffect(() => {
    let isCancelled = false;
    loadMessages("en").then((msgs) => {
      if (!isCancelled) {
        setFallbackMessages(msgs);
      }
    });
    return () => { isCancelled = true; };
  }, []);

  // Load active locale messages
  useEffect(() => {
    let isCancelled = false;
    setIsReady(false);

    loadMessages(locale).then((msgs) => {
      if (!isCancelled) {
        setMessages(msgs);
        setIsReady(true);
      }
    });

    return () => { isCancelled = true; };
  }, [locale]);

  const setLocale = useCallback((newLocale: SupportedLanguage) => {
    if (!SUPPORTED.has(newLocale)) return;
    setLocaleState(newLocale);
    try {
      localStorage.setItem(STORAGE_KEY, newLocale);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const t = useCallback(
    (key: string, params?: TranslationParams): string => {
      // Try active locale first
      const value = resolve(messages, key);
      if (value !== undefined) return interpolate(value, params);

      // Fall back to English
      const fallback = resolve(fallbackMessages, key);
      if (fallback !== undefined) return interpolate(fallback, params);

      // Last resort: return the key itself
      return key;
    },
    [messages, fallbackMessages],
  );

  const contextValue = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t, isReady }),
    [locale, setLocale, t, isReady],
  );

  return (
    <I18nContext.Provider value={contextValue}>
      {children}
    </I18nContext.Provider>
  );
}
